import React from 'react';
import { Ban, Pencil, Wrench, X } from 'lucide-react';
import { ICON_STROKE_WIDTH } from '../../constants';
import {
  formatMaintenanceDateTimeLong,
  getMaintenanceStatusLabel,
} from '../../utils/maintenanceMapper';
import type { MaintenanceWindow } from '../../types/maintenance.types';
import './MachineManagement.css';

interface MachineMaintenanceDetailPanelProps {
  window: MaintenanceWindow;
  onClose: () => void;
  onEdit: () => void;
  onCancelWindow: () => void;
  cancelling?: boolean;
}

const MachineMaintenanceDetailPanel: React.FC<MachineMaintenanceDetailPanelProps> = ({
  window,
  onClose,
  onEdit,
  onCancelWindow,
  cancelling = false,
}) => {
  const locationLabel = window.location.startsWith('Radiology')
    ? window.location
    : `Radiology — ${window.location}`;
  const subtitle = [window.modality, locationLabel].filter(Boolean).join(' · ');
  const canCancel = window.status === 'scheduled' || window.status === 'in_progress';
  const canEdit = window.status === 'scheduled' || window.status === 'in_progress';

  return (
    <aside
      className="machine-edit-panel machine-maintenance-detail-panel"
      aria-label="Maintenance details"
    >
      <header className="machine-edit-panel-header">
        <div className="machine-edit-panel-header-main">
          <div
            className="machine-edit-panel-icon"
            aria-hidden
          >
            <Wrench
              size={18}
              strokeWidth={ICON_STROKE_WIDTH}
            />
          </div>
          <div>
            <h2 className="machine-edit-panel-title">{window.machineName}</h2>
            <p className="machine-edit-panel-subtitle">{subtitle}</p>
          </div>
        </div>
        <button
          type="button"
          className="machine-edit-panel-close"
          onClick={onClose}
          aria-label="Close"
        >
          <X
            size={20}
            strokeWidth={ICON_STROKE_WIDTH}
          />
        </button>
      </header>

      <div className="machine-edit-panel-body machine-maintenance-detail-body">
        <span className={`mm-status-badge mm-status-badge--${window.status}`}>
          {getMaintenanceStatusLabel(window.status)}
        </span>

        <div className="mm-detail-schedule">
          <div className="mm-detail-field">
            <span className="mm-detail-label">Scheduled start</span>
            <span className="mm-detail-value">
              {formatMaintenanceDateTimeLong(window.scheduledStart)}
            </span>
          </div>
          <div className="mm-detail-field">
            <span className="mm-detail-label">Scheduled end</span>
            <span className="mm-detail-value">
              {formatMaintenanceDateTimeLong(window.scheduledEnd)}
            </span>
          </div>
        </div>

        <div className="mm-detail-field mm-detail-field--full">
          <span className="mm-detail-label">Reason / remarks</span>
          <span className="mm-detail-value">{window.reason || '—'}</span>
        </div>

        <div className="mm-detail-grid">
          <div className="mm-detail-field">
            <span className="mm-detail-label">Machine</span>
            <span className="mm-detail-value">{window.machineName}</span>
          </div>
          <div className="mm-detail-field">
            <span className="mm-detail-label">Modality</span>
            <span className="mm-detail-value">{window.modality || '—'}</span>
          </div>
          <div className="mm-detail-field">
            <span className="mm-detail-label">Location</span>
            <span className="mm-detail-value">{locationLabel}</span>
          </div>
          <div className="mm-detail-field">
            <span className="mm-detail-label">Facility</span>
            <span className="mm-detail-value">{window.facilityName || '—'}</span>
          </div>
          <div className="mm-detail-field">
            <span className="mm-detail-label">Scheduled by</span>
            <span className="mm-detail-value">{window.scheduledBy || '—'}</span>
          </div>
          <div className="mm-detail-field">
            <span className="mm-detail-label">Created</span>
            <span className="mm-detail-value">
              {formatMaintenanceDateTimeLong(window.createdAt)}
            </span>
          </div>
        </div>

        <section
          className="mm-audit-section"
          aria-label="Audit history"
        >
          <h3 className="mm-audit-title">Audit history</h3>
          {window.auditHistory.length === 0 ? (
            <p className="mm-audit-empty">No audit entries yet.</p>
          ) : (
            <ul className="mm-audit-list">
              {window.auditHistory.map(entry => (
                <li
                  key={entry.id}
                  className="mm-audit-item"
                >
                  <span className="mm-audit-action">{entry.action}</span>
                  <span className="mm-audit-meta">
                    {formatMaintenanceDateTimeLong(entry.at)}
                    {entry.actor ? ` · ${entry.actor}` : ''}
                  </span>
                  {entry.detail ? <span className="mm-audit-detail">{entry.detail}</span> : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <footer className="machine-edit-panel-footer machine-maintenance-detail-footer">
        <button
          type="button"
          className="machine-dialog-btn machine-dialog-btn-cancel"
          onClick={onClose}
        >
          Close
        </button>
        {canCancel ? (
          <button
            type="button"
            className="machine-dialog-btn mm-btn-cancel-window"
            onClick={onCancelWindow}
            disabled={cancelling}
          >
            <Ban
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
              aria-hidden
            />
            {cancelling ? 'Cancelling…' : 'Cancel window'}
          </button>
        ) : null}
        {canEdit ? (
          <button
            type="button"
            className="machine-dialog-btn machine-dialog-btn-primary"
            onClick={onEdit}
          >
            <Pencil
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
              aria-hidden
            />
            Edit
          </button>
        ) : null}
      </footer>
    </aside>
  );
};

export default MachineMaintenanceDetailPanel;
